import { FormikContext, useFormik } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'

import bookingForLearningItem from '../../../state/calculations/bookingForLearningItem'
import useFinishLearningItem from '../../../state/mutations/useFinishLearningItem'
import useUserLearningItem from '../../../state/queries/useUserLearningItem'
import useDialog from '../../../utilities/useDialog'
import Button from '../../basics/Button/Button'
import { CheckboxFormik } from '../../basics/Checkbox/Checkbox'
import MessageBox from '../../basics/MessageBox/MessageBox'

import styles from './MarkLearningItemAsFinishedDialog.module.scss'

/**********************************************************************
 * MarkLearningItemAsFinishedDialog
 **********************************************************************/
interface MarkLearningItemAsFinishedDialogProps {
  userUuid: string
  learningItemUuid: string
  close: () => void
}

export const MarkLearningItemAsFinishedDialog = React.memo(function MarkLearningItemAsFinishedDialog({
  userUuid,
  learningItemUuid,
  close,
}: MarkLearningItemAsFinishedDialogProps) {
  const { t } = useTranslation()
  const showDialog = useDialog()

  const { userLearningItem } = useUserLearningItem(userUuid, learningItemUuid)

  const booking = bookingForLearningItem(userLearningItem)

  const finishLearningItem = useFinishLearningItem()
  const formik = useFormik({
    initialValues: {
      eventUuid:
        userLearningItem.kind === 'scheduledCourse'
          ? booking
            ? booking.scheduledCourseEventUuid || ''
            : userLearningItem.scheduledCourse.events[0]?.uuid || ''
          : '',
    },
    enableReinitialize: true,
    onSubmit: async (values, { setStatus }) => {
      setStatus('')

      try {
        await finishLearningItem.mutateAsync({
          userUuid,
          courseUuid:
            userLearningItem.kind === 'scheduledCourse'
              ? userLearningItem.scheduledCourse.uuid
              : userLearningItem.kind === 'onDemandCourse'
              ? userLearningItem.onDemandCourse.uuid
              : userLearningItem.kind === 'learningPath'
              ? userLearningItem.uuid
              : '',
          learningItemKind: userLearningItem.kind,
          scheduledCourseEventUuid: values.eventUuid,
        })
      } catch (err) {
        setStatus(String(err))
        return
      }

      await showDialog((close) => (
        <MessageBox close={close}>
          <p>{t('manage learning.mark as finished.learning item was marked as finished')}</p>
        </MessageBox>
      ))

      close()
    },
  })

  return (
    <div className={styles.MarkLearningItemAsFinishedDialog}>
      <FormikContext.Provider value={formik}>
        <form noValidate onSubmit={formik.handleSubmit}>
          <h1>{t('manage learning.mark as finished.headline')}</h1>

          <p>
            {t('manage learning.mark as finished.should learning item {title} be marked as finished', {
              title: userLearningItem.title,
            })}
          </p>

          {userLearningItem.kind === 'scheduledCourse' &&
            !booking &&
            userLearningItem.scheduledCourse.events.length > 1 && (
              <div className={styles.selectEvent}>
                <p>{t('manage learning.mark as finished.please select an event')}</p>
                <ul>
                  {userLearningItem.scheduledCourse.events.map((event) => (
                    <li key={event.uuid}>
                      <CheckboxFormik
                        type="radio"
                        name="eventUuid"
                        value={event.uuid}
                        label={t('format.DATERANGE', { range: { start: event.startDate, end: event.endDate } })}
                        omitErrorDisplay
                        required
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}

          <div className={styles.buttons}>
            <Button type="submit">{t('generic cta.yes')}</Button>
            <Button onClick={() => close()}>{t('generic cta.no')}</Button>
            <div className={styles.errorMessage}>{formik.status}</div>
          </div>
        </form>
      </FormikContext.Provider>
    </div>
  )
})
